import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ContainerType } from '../../pages/orphan-labels/labels/ApiContainerLabels';
import { useApiClient } from 'helpers/api/apiCoreNewClient';

export interface QueryParams {
    [key: string]: any;
}

export interface ServerPageParams {
    sortProperty?: string;
    sortType?: 'asc' | 'desc';
    pageNumber?: string;
    perPage?: string;
}

export interface TableResult<TableValues> {
    result: Array<TableValues & { bt_parcel_id?: number }>;
    total: number;
}

const useServerPagedData = <TableValues extends Object>(
    endpoint: string,
    searchParams?: QueryParams,
    disableLoad: boolean = false,
    requestType: 'get' | 'post' = 'get',
    data?: any,
    showLoading: boolean = false,
    urlSearch: boolean = false,
    sortType: 'asc' | 'desc' = 'desc',
    sortProperty: string | null = null,
) => {
    const initialTableResult: TableResult<TableValues> = useMemo(() => ({ result: [], total: 0 }), []);
    const [tableResult, setTableResult] = useState<TableResult<TableValues>>(initialTableResult);
    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [dataChanged, setDataChanged] = useState<boolean>(urlSearch ? false : true);
    const client = useApiClient()

    const containerTypesWithDefaultSort = [ContainerType.LineHaul, ContainerType.ZoneSubZone, ContainerType.Route];
    const [serverPageParams, setServerPageParams] = useState<ServerPageParams>({
        pageNumber: '0',
        perPage: '10',
        sortProperty: sortProperty ?? (containerTypesWithDefaultSort.includes(searchParams?.containerType) ? 'objectUpdated' : ''),
        sortType: sortType,
    });

    const request = useMemo(
        () => ({
            ...serverPageParams,
            ...searchParams,
        }),
        [serverPageParams, searchParams]
    );

    const getData = useCallback(() => {
        setDataLoading(true);
        if (requestType === 'get' && dataChanged) {
            client[requestType]<TableResult<TableValues>>(endpoint, { params: request, showLoading }).then(
                (response) => {
                    setTableResult(response);
                    setDataLoading(false);
                },
                (reject) => {
                    setTableResult(initialTableResult);
                    setDataLoading(false);
                    return reject;
                }
            );
        }

        if (requestType === 'post' && dataChanged) {
            client[requestType]<TableResult<TableValues>>(endpoint, data, { params: request, showLoading }).then(
                (response) => {
                    setTableResult(response);
                    setDataLoading(false);
                },
                (reject) => {
                    setTableResult(initialTableResult);
                    setDataLoading(false);
                    return reject;
                }
            );
        }
    }, [endpoint, request, initialTableResult,data,dataChanged]);

    useEffect(() => {
        if (!disableLoad && dataChanged) {
            getData();
        }
    }, [endpoint, request, initialTableResult, dataChanged, disableLoad,data]);


    const useDownloadData = (namePrefix: string): [boolean, () => void] => {
        const [loading, setLoading] = useState(false);

        const params = { ...request, toCsv: true };

        const downloadData = () => {
            if (!loading) {
                setLoading(true);
                client.getFile<any>(endpoint, params)
                    .then((response) => {
                        const data = [];
                        data.push(response);
                        const URL = window.URL.createObjectURL(response.data);
                        const anchor = document.createElement('a');

                        anchor.href = URL;
                        const timestamp = format(new Date(), 'yyyy-MM-dd_HH.mm.ss.SSS');
                        anchor.download = `${namePrefix}_${timestamp}.xlsx`;

                        document.body.appendChild(anchor);
                        anchor.click();
                        document.body.removeChild(anchor);
                        window.URL.revokeObjectURL(URL);
                        setLoading(false);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        };

        return [loading, downloadData];
    };

    const resetPageNumber = () => {
        setServerPageParams((prev) => ({
            ...prev,
            pageNumber: '0',
        }));
    };

    const updateList = useCallback(
        (fn: (values: TableValues[]) => TableValues[]) => {
            setTableResult(({ result, total }) => {
                return {
                    result: fn(result),
                    total,
                };
            });
        },
        [tableResult, setTableResult]
    );

    const updateListByIndex = useCallback(
        (index, newRecord) => {
            setTableResult(({ result, total }) => {
                const newResult = [...result];
                newResult[index] = newRecord;
                return {
                    result: [...newResult],
                    total,
                };
            });
        },
        [tableResult, setTableResult]
    );

    return {
        tableResult,
        dataLoading,
        serverPageParams,
        setServerPageParams,
        getData,
        useDownloadData,
        resetPageNumber,
        updateListByIndex,
        updateList,
        dataChanged,
        setDataChanged
    };
};

export default useServerPagedData;